import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { loadGenres } from '../../utils/genres';

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminGenres() {
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get('return');
  const bookReturn = /^\/admin\/books\/[a-f0-9]{24}$/i.test(returnTo || '') ? returnTo : null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const ping = (m) => { setToast(m); setTimeout(() => setToast(''), 1800); };

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await axios.get('/api/admin/genres', { headers: authHeaders() });
      setItems(Array.isArray(data) ? data : []);
      loadGenres(true);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true); setErr('');
    try {
      await axios.post('/api/admin/genres', { name }, { headers: authHeaders() });
      setNewName('');
      ping('Created');
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const startRename = (g) => setEditing({ name: g.name, draft: g.name });
  const cancelRename = () => setEditing(null);
  const commitRename = async () => {
    if (!editing) return;
    const target = editing.draft.trim();
    if (!target || target === editing.name) { setEditing(null); return; }
    try {
      const { data } = await axios.put(
        `/api/admin/genres/${encodeURIComponent(editing.name)}`,
        { name: target },
        { headers: authHeaders() }
      );
      ping(data.moved ? `Renamed (${data.moved} books moved)` : 'Renamed');
      setEditing(null);
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Rename failed');
    }
  };

  const onDelete = async (g) => {
    const fallback = g.count > 0
      ? window.prompt(
          `"${g.name}" has ${g.count} book${g.count === 1 ? '' : 's'}. Move them to which genre? (leave blank to clear)`,
          ''
        )
      : '';
    if (fallback === null) return;
    const trimmed = (fallback || '').trim();
    try {
      const { data } = await axios.delete(
        `/api/admin/genres/${encodeURIComponent(g.name)}?fallback=${encodeURIComponent(trimmed)}`,
        { headers: authHeaders() }
      );
      const target = trimmed || 'cleared';
      ping(data.moved ? `Deleted (${data.moved} books → ${target})` : 'Deleted');
      load();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Delete failed');
    }
  };

  const totalBooks = items.reduce((s, g) => s + (g.count || 0), 0);

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1 className="admin-page-title">Genres</h1>
        <p className="admin-page-sub">{items.length} genres · {totalBooks} books across them.</p>
        {bookReturn && <Link className="admin-btn admin-btn-ghost admin-btn-sm" to={bookReturn}>← Back to book</Link>}
      </header>

      <div className="admin-card">
        <h2>Add a genre</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div className="admin-field" style={{ flex: 1, marginBottom: 0 }}>
            <label className="admin-label">Name</label>
            <input
              className="admin-input"
              value={newName}
              placeholder="e.g. Adventure, Mystery, Folktale"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
            />
          </div>
          <button type="button" className="admin-btn" disabled={!newName.trim() || creating} onClick={create}>
            {creating ? 'Creating…' : '+ Add'}
          </button>
        </div>
      </div>

      {err && <div className="admin-toast admin-toast-error">{err}</div>}
      {toast && <div className="admin-toast">{toast}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Books</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} style={{ textAlign: 'center', padding: 30 }}>Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={3} style={{ textAlign: 'center', padding: 30, opacity: 0.7 }}>No genres yet.</td></tr>
            ) : items.map((g) => {
              const isEditing = editing && editing.name === g.name;
              return (
                <tr key={g.name}>
                  <td>
                    {isEditing ? (
                      <input
                        className="admin-input"
                        autoFocus
                        value={editing.draft}
                        onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') cancelRename(); }}
                      />
                    ) : (
                      <strong style={{ color: 'var(--admin-fg-strong)' }}>{g.name}</strong>
                    )}
                  </td>
                  <td>{g.count || 0}</td>
                  <td>
                    <div className="admin-row-actions">
                      {isEditing ? (
                        <>
                          <button type="button" className="admin-btn admin-btn-sm" onClick={commitRename}>Save</button>
                          <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={cancelRename}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => startRename(g)}>Rename</button>
                          <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => onDelete(g)}>Delete</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
